import React, { useEffect, useRef, useContext } from 'react';
import analytics from '../analytics/analytics';
import { ChatbotDataContext } from '../context/ChatbotDataContext';
export const DialogContentWrapper = ({
  ariaDescription,
  chatContainerHeight,
  children,
  isAlertDialogActive
}) => {
  const {
    isMobile
  } = useContext(ChatbotDataContext);
  const styleRef = useRef(null);
  const dialogRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const originalStylesRef = useRef({});
  useEffect(() => {
    let mounted = true;
    const cleanupStyles = () => {
      try {
        // Remove style element
        if (styleRef.current) {
          styleRef.current.remove();
          styleRef.current = null;
        }
        // Restore original dialog styles
        const dialog = dialogRef.current;
        if (dialog && originalStylesRef.current.dialog) {
          Object.entries(originalStylesRef.current.dialog).forEach(([key, value]) => {
            if (value !== undefined) {
              dialog.style[key] = value;
            }
          });
        }
        // Reset dialog content wrapper styles
        const contentWrapper = contentWrapperRef.current;
        if (contentWrapper && originalStylesRef.current.contentWrapper) {
          Object.entries(originalStylesRef.current.contentWrapper).forEach(([key, value]) => {
            if (value !== undefined) {
              contentWrapper.style[key] = value;
            }
          });
        }
        // Clear refs only if component is unmounting
        if (!mounted) {
          dialogRef.current = null;
          contentWrapperRef.current = null;
          originalStylesRef.current = {};
        }
      } catch (err) {
        analytics.logError({
          message: 'DialogContentWrapper Cleanup Error',
          error: err instanceof Error ? err.message : JSON.stringify(err),
          context: {
            ariaDescription,
            isAlertDialogActive,
            mounted
          }
        });
      }
    };
    try {
      const dialog = document.querySelector(`div[aria-describedby="${ariaDescription}"]`);
      if (!dialog || !(dialog instanceof HTMLDivElement)) {
        cleanupStyles();
        return;
      }
      dialogRef.current = dialog;
      // Store original styles before modification
      originalStylesRef.current.dialog = {
        height: dialog.style.height || undefined,
        margin: dialog.style.margin || undefined,
        marginTop: dialog.style.marginTop || undefined,
        borderBottomRightRadius: dialog.style.borderBottomRightRadius || undefined,
        borderBottomLeftRadius: dialog.style.borderBottomLeftRadius || undefined
      };
      // Apply base dialog styles
      if (isMobile) {
        dialog.style.height = 'calc(100% - 25px)';
        dialog.style.margin = '0px';
        dialog.style.marginTop = '25px';
        dialog.style.borderBottomRightRadius = '0px';
        dialog.style.borderBottomLeftRadius = '0px';
      } else {
        dialog.style.height = chatContainerHeight;
      }
      // Handle dialog content wrapper
      const contentWrapper = dialog.querySelector('div');
      if (contentWrapper instanceof HTMLDivElement) {
        contentWrapperRef.current = contentWrapper;
        originalStylesRef.current.contentWrapper = {
          height: contentWrapper.style.height || undefined,
          display: contentWrapper.style.display || undefined
        };
        contentWrapper.style.height = '100%';
        contentWrapper.style.display = 'block';
      }
      // Create and apply pointer event styles
      const style = document.createElement('style');
      styleRef.current = style;
      style.innerHTML = isAlertDialogActive ? `
          div[role="alertdialog"] {
            pointer-events: auto !important;
          }
          div[role="dialog"] {
            pointer-events: none !important;
          }
        ` : `
          iframe#px-captcha-modal {
            pointer-events: auto;
          }
          div[role="dialog"] {
            pointer-events: auto !important;
          }
        `;
      document.head.appendChild(style);
    } catch (err) {
      cleanupStyles();
      analytics.logError({
        message: 'DialogContentWrapper Error',
        error: err instanceof Error ? err.message : JSON.stringify(err),
        context: {
          ariaDescription,
          isAlertDialogActive
        }
      });
    }
    return () => {
      mounted = false;
      cleanupStyles();
    };
  }, [isAlertDialogActive, isMobile, chatContainerHeight, ariaDescription]);
  return React.createElement(React.Fragment, null, children);
};